import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdminPanelSettings, Block, CancelOutlined, Calculate, CheckCircleOutline, Edit, LockOpen, RestartAlt } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { adminUsersService, AdminClientUser, AdminClientDetail, AdminPlanOption } from '@services/admin-users.service.ts';
import { ConnectionsByStatusChips } from '@components/admin/ConnectionsByStatusChips.tsx';
import { toastService } from '@services/toast.service.ts';
import { AdminClientDetailModal } from '@components/admin/AdminClientDetailModal.tsx';
import { AdminChangePlanModal } from '@components/admin/AdminChangePlanModal.tsx';
import { AdminUserRolesModal } from '@components/admin/AdminUserRolesModal.tsx';
import { UserStatusChip } from '@components/admin/UserStatusChip.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

const AdminClientsPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [users, setUsers] = useState<AdminClientUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedClient, setSelectedClient] = useState<AdminClientDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [planOptions, setPlanOptions] = useState<AdminPlanOption[]>([]);
  const [planModalUser, setPlanModalUser] = useState<AdminClientUser | null>(null);
  const [statusConfirmUser, setStatusConfirmUser] = useState<AdminClientUser | null>(null);
  const [resetSubConfirmUser, setResetSubConfirmUser] = useState<AdminClientUser | null>(null);
  const [resettingSubscription, setResettingSubscription] = useState(false);
  const [rolesModalUser, setRolesModalUser] = useState<AdminClientUser | null>(null);
  // Перерахунок цілей одного клієнта (2026-10-03; масового поки нема - рішення користувача).
  const [normsConfirmUser, setNormsConfirmUser] = useState<AdminClientUser | null>(null);

  const handleConfirmRecalculateNorms = async () => {
    if (!normsConfirmUser) return;
    const user = normsConfirmUser;
    setNormsConfirmUser(null);
    try {
      await adminUsersService.recalculateClientDailyNorms(user.id);
      toastService.success(`Денні норми ${user.name} перераховано`);
    } catch {
      toastService.error('Не вдалося перерахувати денні норми');
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminUsersService.getClients({
        page,
        limit,
        search: searchQuery || undefined,
        planCode: planFilter || undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
      });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список клієнтів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, planFilter, statusFilter]);

  const handleConfirmStatusToggle = async () => {
    if (!statusConfirmUser) return;
    const newStatus = statusConfirmUser.status === 1 ? 2 : 1;
    try {
      await adminUsersService.setUserStatus('clients', statusConfirmUser.id, newStatus);
      setUsers((prev) => prev.map((u) => (u.id === statusConfirmUser.id ? { ...u, status: newStatus } : u)));
      toastService.success(newStatus === 2 ? 'Клієнта заблоковано.' : 'Клієнта розблоковано.');
    } catch {
      toastService.error('Не вдалося змінити статус клієнта.');
    } finally {
      setStatusConfirmUser(null);
    }
  };

  const handleConfirmResetSubscription = async () => {
    if (!resetSubConfirmUser) return;
    setResettingSubscription(true);
    try {
      await adminUsersService.resetClientSubscription(resetSubConfirmUser.id);
      toastService.success(`Підписку клієнта "${resetSubConfirmUser.name}" скинуто до нульового стану.`);
      fetchUsers();
    } catch {
      toastService.error('Не вдалося скинути підписку клієнта.');
    } finally {
      setResettingSubscription(false);
      setResetSubConfirmUser(null);
    }
  };

  const handleRolesChanged = (userId: number, roles: string[]) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, roles } : u)));
    setRolesModalUser((prev) => (prev && prev.id === userId ? { ...prev, roles } : prev));
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    adminUsersService.getClientPaymentsOptions().then((opts) => setPlanOptions(opts.plans));
  }, []);

  const handleRowClick = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const detail = await adminUsersService.getClientById(id);
      setSelectedClient(detail);
    } catch {
      toastService.error('Не вдалося завантажити деталі клієнта.');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setTimeout(() => setSelectedClient(null), 300);
  };

  const handlePlanChange = async (planId: number) => {
    if (!planModalUser) return;
    await adminUsersService.setClientPlan(planModalUser.id, planId);
    const plan = planOptions.find((p) => p.value === planId);
    setUsers((prev) => prev.map((u) =>
      u.id === planModalUser.id
        ? { ...u, subscription: plan ? { planName: plan.label, planCode: plan.code, status: 'active' } : u.subscription }
        : u,
    ));
    toastService.success('Тарифний план змінено.');
  };

  return (
    <Box data-i-doc={PageIndices.ADMIN_CLIENTS}>
      <PageHint iDoc={PageIndices.ADMIN_CLIENTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-clients"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'planCode',
              type: 'select',
              label: 'Тариф',
              value: planFilter,
              onChange: (v) => { setPlanFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                ...planOptions.map((p) => ({ value: p.code, label: p.label })),
              ],
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => { setStatusFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: '1', label: 'Активний' },
                { value: '2', label: 'Заблокований' },
                { value: '3', label: 'Видалений' },
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setPlanFilter(''); setStatusFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Клієнтів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Часовий пояс</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Експертів</TableCell>
                  <TableCell>Реєстрація</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  return (
                    <TableRow
                      hover
                      key={user.id}
                      onClick={() => handleRowClick(user.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{user.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <UserAvatar path={user.avatar} name={user.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          {user.isEmailVerified ? (
                            <Tooltip title="Email верифіковано">
                              <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email не верифіковано">
                              <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {user.phone && (
                          <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>{user.timezone ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <UserStatusChip status={user.status} isProtectedDemoAccount={user.isProtectedDemoAccount} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PlanChip subscription={user.subscription} billingExempt={user.billingExempt} />
                          <Tooltip title="Змінити план">
                            <IconButton size="small" onClick={() => setPlanModalUser(user)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell><ConnectionsByStatusChips connectionsByStatus={user.connectionsByStatus} /></TableCell>
                      <TableCell>{new Date(user.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {user.status === 1 && (
                          <Tooltip title="Заблокувати">
                            <IconButton size="small" color="error" onClick={() => setStatusConfirmUser(user)}>
                              <Block fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.status === 2 && (
                          <Tooltip title="Розблокувати">
                            <IconButton size="small" color="success" onClick={() => setStatusConfirmUser(user)}>
                              <LockOpen fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Скидання фінансового стану акаунту">
                          <IconButton size="small" onClick={() => setResetSubConfirmUser(user)}>
                            <RestartAlt fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Перерахувати денні норми">
                          <IconButton size="small" onClick={() => setNormsConfirmUser(user)}>
                            <Calculate fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Керувати ролями">
                          <IconButton size="small" onClick={() => setRolesModalUser(user)}>
                            <AdminPanelSettings fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-clients"
        />
      </PageCard>

      <AdminClientDetailModal
        client={detailLoading ? null : selectedClient}
        open={detailOpen}
        onClose={handleDetailClose}
        onStatusChanged={(id, status) => {
          setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, status } : u)));
          setSelectedClient((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
        }}
      />

      <AdminChangePlanModal
        open={!!planModalUser}
        onClose={() => setPlanModalUser(null)}
        onSave={handlePlanChange}
        plans={planOptions}
        currentPlanCode={planModalUser?.subscription?.planCode ?? null}
        userName={planModalUser?.name ?? ''}
      />

      <ConfirmationDialog
        open={!!statusConfirmUser}
        onClose={() => setStatusConfirmUser(null)}
        onConfirm={handleConfirmStatusToggle}
        title={statusConfirmUser?.status === 1 ? 'Заблокувати клієнта?' : 'Розблокувати клієнта?'}
        message={
          statusConfirmUser?.status === 1
            ? `${statusConfirmUser?.name} втратить доступ до застосунку. Дані не видаляються — дію можна скасувати.`
            : `${statusConfirmUser?.name} знову зможе увійти в застосунок.`
        }
        confirmText={statusConfirmUser?.status === 1 ? 'Заблокувати' : 'Розблокувати'}
      />

      <ConfirmationDialog
        open={!!normsConfirmUser}
        onClose={() => setNormsConfirmUser(null)}
        onConfirm={handleConfirmRecalculateNorms}
        title="Перерахувати денні норми клієнта?"
        message={`Денні норми ${normsConfirmUser?.name ?? ''} (вода, калорії, БЖВ) буде перераховано за поточними даними профілю й вимірювань. Якщо клієнт змінював норми вручну - їх буде замінено. Порівняти до перерахунку можна у вкладці Розрахунок денних норм.`}
        confirmText="Перерахувати"
      />

      <ConfirmationDialog
        open={!!resetSubConfirmUser}
        onClose={() => setResetSubConfirmUser(null)}
        onConfirm={handleConfirmResetSubscription}
        title="Скидання фінансового стану акаунту?"
        message={
          `Підписку клієнта "${resetSubConfirmUser?.name ?? ''}" буде видалено, прапорці "тріал використано" ` +
          'і billingExempt скинуто — гарантований нульовий стан з точки зору білінгу, як у першого дня, ' +
          'навіть якщо раніше були ручні пільги. Накопичені дані застосунку (щоденники, вимірювання тощо) ' +
          'не зачіпаються. Дію не можна відмінити.'
        }
        confirmText={resettingSubscription ? 'Скидання...' : 'Перевести'}
      />

      <AdminUserRolesModal
        open={!!rolesModalUser}
        onClose={() => setRolesModalUser(null)}
        userId={rolesModalUser?.id ?? null}
        userName={rolesModalUser?.name ?? ''}
        roles={rolesModalUser?.roles ?? []}
        onChanged={handleRolesChanged}
        visibleRoles={['developer', 'tester']}
      />
    </Box>
  );
};

export const AdminClientsPage: React.FC = () => (
  <DashboardLayout>
    <AdminClientsPageContent />
  </DashboardLayout>
);
